<template>
    <div class="foto-perfil">
        <div class="foto-perfil__marco" :class="{'foto-perfil__marco--vacio': !imagen}">
            <img v-if="imagen" :src="imagen" :alt="'Fotografía de ' + nombre">
            <span v-else class="foto-perfil__iniciales">{{ iniciales }}</span>
            <v-progress-circular v-if="cargando" indeterminate color="white" class="foto-perfil__cargando"/>
        </div>

        <div class="foto-perfil__acciones">
            <div class="foto-perfil__titulo">Fotografía del perfil</div>
            <p class="foto-perfil__ayuda">
                Permite reconocer de quién es la cuenta. Use una foto reciente, de frente y con el rostro visible;
                se recorta sola al centro.
            </p>

            <v-chip v-if="!imagen" x-small label color="warning" text-color="white" class="mb-2">
                <v-icon x-small left>no_photography</v-icon> Sin fotografía
            </v-chip>

            <div>
                <v-btn small outlined color="primary" :disabled="cargando || !personaId" @click="$refs.archivo.click()">
                    <v-icon small left>photo_camera</v-icon> {{ imagen ? 'Cambiar foto' : 'Subir foto' }}
                </v-btn>
                <v-btn v-if="imagen" small text color="error" class="ml-1" :disabled="cargando" @click="quitar">
                    Quitar
                </v-btn>
            </div>

            <input ref="archivo" type="file" accept="image/jpeg,image/png,image/webp" hidden @change="elegida">
        </div>
    </div>
</template>

<script>
/**
 * Fotografía del perfil de una cuenta administrativa.
 *
 * La imagen se recorta al centro en cuadrado y se reduce a 512 px en el
 * navegador antes de subirla: todas quedan con el mismo formato y peso, y el
 * servidor no necesita procesar imágenes.
 *
 *   <foto-perfil :persona-id="id" :url="persona.foto_url" :nombre="..." @cambio="url => ..."/>
 */
const LADO = 512;

export default {
    name: "FotoPerfil",
    props: {
        personaId: {type: [Number, String], default: null},
        url: {type: String, default: null},
        nombre: {type: String, default: ""},
    },
    data() {
        return {imagen: this.url, cargando: false};
    },
    computed: {
        iniciales() {
            const partes = (this.nombre || "").trim().split(/\s+/).filter(Boolean);
            return ((partes[0] || "?")[0] + ((partes[1] || "")[0] || "")).toUpperCase();
        },
    },
    watch: {
        url(valor) {
            this.imagen = valor;
        },
    },
    methods: {
        elegida(evento) {
            const archivo = evento.target.files[0];
            evento.target.value = "";

            if (!archivo) return;

            if (!/^image\/(jpeg|png|webp)$/.test(archivo.type)) {
                this.$toast.error("La fotografía debe ser JPG, PNG o WEBP.");
                return;
            }

            this.cargando = true;

            this.recortar(archivo)
                .then(blob => this.subir(blob))
                .catch(error => {
                    this.$toast.error(error.message || "No se pudo leer la imagen.");
                    this.cargando = false;
                });
        },
        // Recorte cuadrado al centro y reducción a LADO px, en JPEG.
        recortar(archivo) {
            return new Promise((resolve, reject) => {
                const url = URL.createObjectURL(archivo);
                const img = new Image();

                img.onload = () => {
                    URL.revokeObjectURL(url);
                    const lado = Math.min(img.naturalWidth, img.naturalHeight);

                    if (lado < 200) {
                        reject(new Error("La fotografía es muy pequeña: use una de al menos 200 × 200 píxeles."));
                        return;
                    }

                    const destino = Math.min(LADO, lado);
                    const lienzo = document.createElement("canvas");
                    lienzo.width = destino;
                    lienzo.height = destino;

                    const ctx = lienzo.getContext("2d");
                    // Fondo blanco: un PNG transparente no debe quedar negro al pasar a JPEG.
                    ctx.fillStyle = "#fff";
                    ctx.fillRect(0, 0, destino, destino);
                    ctx.drawImage(img, (img.naturalWidth - lado) / 2, (img.naturalHeight - lado) / 2, lado, lado, 0, 0, destino, destino);

                    lienzo.toBlob(blob => blob ? resolve(blob) : reject(new Error("No se pudo procesar la imagen.")), "image/jpeg", 0.88);
                };

                img.onerror = () => {
                    URL.revokeObjectURL(url);
                    reject(new Error("El archivo no es una imagen válida."));
                };

                img.src = url;
            });
        },
        subir(blob) {
            const datos = new FormData();
            datos.append("persona_id", this.personaId);
            datos.append("foto", blob, "foto.jpg");

            return window.axios.post("/personas/foto", datos).then(response => {
                if (response.data.success) {
                    this.imagen = response.data.data.foto_url;
                    this.$toast.success(response.data.message);
                    this.$emit("cambio", this.imagen);
                } else {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(error => {
                    const cuerpo = error.response && error.response.data;
                    const detalle = cuerpo && cuerpo.data && typeof cuerpo.data === "object" && !Array.isArray(cuerpo.data)
                        ? Object.values(cuerpo.data).flat().join(" ") : (cuerpo && cuerpo.message);
                    this.$toast.error(detalle || "No se pudo guardar la fotografía.");
                })
                .finally(() => {
                    this.cargando = false;
                });
        },
        quitar() {
            this.cargando = true;

            window.axios.post("/personas/foto/eliminar", {persona_id: this.personaId}).then(response => {
                if (response.data.success) {
                    this.imagen = null;
                    this.$toast.success(response.data.message);
                    this.$emit("cambio", null);
                } else {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(() => this.$toast.error("No se pudo quitar la fotografía."))
                .finally(() => {
                    this.cargando = false;
                });
        },
    },
}
</script>

<style lang="scss" scoped>
.foto-perfil {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 18px;
    padding: 4px 0 16px;
    margin-bottom: 8px;
    border-bottom: 1px solid #eef0f6;
}

.foto-perfil__marco {
    position: relative;
    width: 132px;
    height: 132px;
    border-radius: 50%;
    overflow: hidden;
    flex: none;
    border: 3px solid #fff;
    box-shadow: 0 0 0 2px #203d89;
    background: #e9edf7;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }

    &--vacio {
        box-shadow: 0 0 0 2px #c8893a;
        display: flex;
        align-items: center;
        justify-content: center;
    }
}

.foto-perfil__iniciales {
    font-size: 2.6rem;
    font-weight: 700;
    color: #203d89;
    letter-spacing: 0.02em;
}

.foto-perfil__cargando {
    position: absolute;
    inset: 0;
    margin: auto;
    background: rgba(27, 35, 64, 0.45);
    border-radius: 50%;
}

.foto-perfil__acciones {
    flex: 1;
    min-width: 240px;
}

.foto-perfil__titulo {
    font-weight: 700;
    color: #1b2340;
}

.foto-perfil__ayuda {
    font-size: 0.8rem;
    color: #5b6380;
    margin: 2px 0 8px;
    max-width: 520px;
}
</style>
